import { useEffect, useRef, useState, type RefObject } from "react";
import { Link } from "react-router";
import { MITTEILUNGEN, useDialog } from "../dialog";
import { fehlertext } from "../fehler";
import { gefiltert, MITTEILUNGEN_PFAD, useAlleGelesen, useMitteilungen, type Filter } from "../mitteilungen";
import { Knopf } from "../ui/Knopf";
import { Laedt } from "../ui/Laedt";
import { Meldung } from "../ui/Leer";
import { Segment } from "../ui/Segment";
import { Icon } from "./Icon";
import { MitteilungenListe } from "./MitteilungenListe";
import s from "./mitteilungen.module.css";

const FILTER = [
  { wert: "alle", label: "Alle" },
  { wert: "ungelesen", label: "Ungelesen" },
] as const;

/** Mitteilungen als Leiste rechts über der aktuellen Seite (KOPFZEILE.md, Abschnitt 3.4). Hängt wie die Dialoge an der Adresse
 *  (?mitteilungen=1): Zurück schließt sie, und es ist immer nur eins offen. <dialog> hält den Fokus in der Leiste, Escape schließt.
 *  Beim Öffnen steht der Fokus auf der Überschrift, beim Schließen wieder auf der Glocke (`glocke` ist ihre id). */
export function MitteilungenLeiste({ glocke }: { glocke: string }) {
  const { offen, schliessen } = useDialog(MITTEILUNGEN);
  const ref = useRef<HTMLDialogElement>(null);
  const titel = useRef<HTMLHeadingElement>(null);
  const gedrueckt = useRef<EventTarget | null>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (offen && !d.open) {
      d.showModal();
      titel.current?.focus();
    }
    if (!offen && d.open) {
      d.close();
      // Öffnet ein Klick die Aufgabe, merkt sich ihr Dialog die Glocke und gibt den Fokus beim Schließen dorthin zurück.
      document.getElementById(glocke)?.focus();
    }
  }, [offen, glocke]);

  return (
    <dialog
      ref={ref}
      className={s.leiste}
      aria-labelledby="mitteilungen-titel"
      onCancel={(e) => {
        e.preventDefault();
        schliessen();
      }}
      onPointerDown={(e) => {
        gedrueckt.current = e.target;
      }}
      onClick={(e) => {
        // Klick daneben: Das Ziel ist dann der Hintergrund des Dialogs selbst, nicht sein Inhalt. Auch das Drücken muss dort gewesen sein,
        // sonst schließt ein Markieren von Text, das außerhalb endet.
        if (e.target === e.currentTarget && gedrueckt.current === e.currentTarget) schliessen();
      }}
    >
      {offen ? <Inhalt titel={titel} onSchliessen={schliessen} /> : null}
    </dialog>
  );
}

function Inhalt({ titel, onSchliessen }: { titel: RefObject<HTMLHeadingElement | null>; onSchliessen: () => void }) {
  const liste = useMitteilungen();
  const alle = useAlleGelesen();
  const [filter, setFilter] = useState<Filter>("alle");
  const ungelesen = (liste.data ?? []).filter((m) => !m.gelesen_am).length;

  return (
    <div className={s.rahmen}>
      <div className={s.kopf}>
        <h2 id="mitteilungen-titel" ref={titel} tabIndex={-1}>
          Mitteilungen <span>{ungelesen > 0 ? `${ungelesen} ungelesen` : "alles gelesen"}</span>
        </h2>
        <button type="button" className={s.zu} aria-label="Schließen" onClick={onSchliessen}>
          <Icon name="schliessen" />
        </button>
      </div>
      <div className={s.werkzeug}>
        <Segment legende="Mitteilungen anzeigen" optionen={FILTER} wert={filter} onWechsel={setFilter} />
        {ungelesen > 0 ? (
          <Knopf klein onClick={() => alle.mutate()} disabled={alle.isPending}>
            Alle gelesen
          </Knopf>
        ) : null}
      </div>
      <div className={s.inhalt}>
        {liste.isPending ? <Laedt /> : null}
        {liste.isError ? <Meldung fehler>{fehlertext(liste.error)}</Meldung> : null}
        {liste.data ? (
          <MitteilungenListe
            liste={gefiltert(liste.data, filter)}
            ueber="leiste"
            leer={<p className={s.leer}>{filter === "ungelesen" && liste.data.length > 0 ? "Alles gelesen." : "Noch keine Mitteilungen."}</p>}
          />
        ) : null}
      </div>
      <div className={s.fuss}>
        <Link to={MITTEILUNGEN_PFAD} replace>
          Alle Mitteilungen auf einer Seite
        </Link>
      </div>
    </div>
  );
}
